import React from 'react';
import { AlertCircle, RefreshCcw, Home } from 'lucide-react';
import { Button } from './primitives';
import { Alert, AlertDescription } from './primitives';
import { track } from '../lib/productEvents';

/**
 * ErrorBoundary Component
 *
 * Catches JavaScript errors anywhere in the child component tree,
 * logs those errors, and displays a fallback UI instead of crashing the app.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(_error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    track('client_error', { where: 'boundary' });

    // A failed dynamic import almost always means this tab is running a stale
    // build (chunk hashes changed after a deploy). Reload ONCE to fetch the
    // fresh index + chunks. lazyWithRetry handles most of these before they
    // reach here; this is the safety net for any that slip through.
    if (this.isChunkLoadError(error)) {
      let last = 0;
      try { last = Number(sessionStorage.getItem('chunkReloadAt') || 0); } catch { /* ignore */ }
      if (Date.now() - last > 10_000) {
        try { sessionStorage.setItem('chunkReloadAt', String(Date.now())); } catch { /* ignore */ }
        window.location.reload();
        return;
      }
    }

    this.setState({ error, errorInfo });
  }

  isChunkLoadError = (error) => {
    const msg = String(error?.message || error || '');
    return (
      /dynamically imported module/i.test(msg) ||
      /Importing a module script failed/i.test(msg) ||
      /error loading dynamically imported module/i.test(msg) ||
      /chunkloaderror/i.test(msg) ||
      /failed to fetch dynamically imported module/i.test(msg)
    );
  };

  handleReset = () => {
    // A full reload is the reliable recovery (especially for stale-chunk
    // errors) — re-fetches the current index and chunks.
    try {
      // Clear any cached chunk reload attempts
      sessionStorage.removeItem('chunkReloadAt');
    } catch {
      // Ignore errors when clearing session storage
    }
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      // Render fallback UI
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
          <div className="max-w-2xl w-full">
            <Alert variant="destructive" className="mb-6">
              <AlertCircle className="h-5 w-5" />
              <AlertDescription>
                <h2 className="text-xl font-bold mb-2">Something went wrong</h2>
                <p className="mb-4">
                  We&apos;re sorry, but something unexpected happened. The error has been logged and we&apos;ll look into it.
                </p>

                {this.state.error && (
                  <details className="mt-4 mb-4" role="region" aria-labelledby="error-details-summary">
                    <summary 
                      id="error-details-summary" 
                      className="cursor-pointer font-semibold text-sm hover:underline"
                      role="button"
                      tabIndex="0"
                      aria-expanded="false"
                      aria-controls="error-details-content"
                    >
                      Technical Details (for developers)
                    </summary>
                    <div 
                      id="error-details-content" 
                      className="mt-2 p-4 bg-gray-100 rounded-md font-mono text-xs overflow-auto max-h-60"
                      aria-hidden="true"
                    >
                      <p className="font-bold mb-2">Error Message:</p>
                      <p className="mb-4 text-red-600">{this.state.error.toString()}</p>

                      {this.state.errorInfo && (
                        <>
                          <p className="font-bold mb-2">Component Stack:</p>
                          <pre className="whitespace-pre-wrap text.body">
                            {this.state.errorInfo.componentStack}
                          </pre>
                        </>
                      )}
                    </div>
                  </details>
                )}
              </AlertDescription>
            </Alert>

            <div className="flex gap-3">
              <Button onClick={this.handleReset} variant="default">
                <RefreshCcw className="mr-2 h-4 w-4" />
                Try Again
              </Button>
              <Button onClick={this.handleGoHome} variant="outline">
                <Home className="mr-2 h-4 w-4" />
                Go Home
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
